import { $, bindClick, authHash, setAuthHash, isAuthHash } from "./lib/dom.js";
import { PROVIDER_HINT } from "./lib/labels.js";
import {
  LS, persistAuth, portalToken, authMode, setAuthSession, clearAuthSession,
  setEnv, api, apiPublic, resolveApiBase, isLocalDevHost,
} from "./api.js";
import { authProviders } from "./state.js";
import { defaultConsoleRoute } from "./nav.js";

let pollTimer = null;
let qrSession = { id: null, intent: "login", mode: "mock" };
let qrFinishing = false;
let loginProvider = "wechat";
let regProvider = "wechat";
let _hooks = {};
/** 会话失效后自动换码；防止连续 404 刷爆 /start */
let _qrAutoRefreshing = false;

function errMsg(data, fallback) {
  if (!data) return fallback;
  if (typeof data.error === "string" && data.error) return data.error;
  if (data.error && data.error.message) return data.error.message;
  if (data.message) return data.message;
  return fallback;
}

function errCode(data) {
  if (!data) return "";
  if (data.error && data.error.code) return String(data.error.code);
  if (data.code) return String(data.code);
  return "";
}

export function initAuthGate(hooks) {
  _hooks = hooks || {};
}

function refreshAll() {
  return _hooks.refreshAll && _hooks.refreshAll();
}

function ping() {
  return _hooks.ping && _hooks.ping();
}

export function applyPublicBaseFromProviders() {
  const pub = String((authProviders && authProviders.public_base_url) || "")
    .trim()
    .replace(/\/$/, "");
  if (!pub) return;
  const base = $("base-url");
  if (!base) return;
  const origin = (window.location.origin || "").replace(/\/$/, "");
  const cur = (base.value || "").trim().replace(/\/$/, "");
  // 控制台由 FIT-1 同源托管时，不写 LAN 地址（避免 127.0.0.1 ↔ 192.168.x CORS）
  if (origin) {
    const resolved = resolveApiBase(origin, pub);
    if (resolved === origin) {
      base.value = origin;
      localStorage.setItem(LS.base, origin);
      return;
    }
  }
  const loopback = /^https?:\/\/(127\.0\.0\.1|localhost|\[::1\])(:\d+)?$/i;
  if (!cur || loopback.test(cur)) {
    base.value = pub;
    localStorage.setItem(LS.base, pub);
  }
}

export function loadAuth() {
  const base = $("base-url");
  if (base) {
    const origin = (window.location.origin || "").replace(/\/$/, "");
    const saved = (localStorage.getItem(LS.base) || "").trim().replace(/\/$/, "");
    const resolved = resolveApiBase(origin, saved) || origin;
    base.value = resolved;
    if (resolved) localStorage.setItem(LS.base, resolved);
  }
  const mk = $("merchant-key");
  if (mk) mk.value = localStorage.getItem(LS.key) || "";
  const lk = $("live-key");
  if (lk) lk.value = localStorage.getItem(LS.liveKey) || "";
  const mid = $("merchant-id");
  if (mid) mid.value = localStorage.getItem(LS.merchantId) || "";
  setEnv(localStorage.getItem(LS.env) || "sandbox");
}

const SSO_TARGET_KEY = "fit1_mc_sso_target";

const SSO_HOST_ALLOW = [
  "app.ducai.cc",
  "api.ducai.cc",
  "dist.ducai.cc",
  "localhost",
  "127.0.0.1",
  "[::1]",
  "8.130.161.248",
];

/** 开放平台统一登录目标地址：?target= / ?return_to= */
export function consumeSsoTarget() {
  let target = "";
  try {
    const u = new URL(window.location.href);
    target = (u.searchParams.get("target") || u.searchParams.get("return_to") || "").trim();
    const hash = (u.hash || "").replace(/^#/, "");
    if (!target && hash.includes("?")) {
      const q = new URLSearchParams(hash.slice(hash.indexOf("?") + 1));
      target = (q.get("target") || q.get("return_to") || "").trim();
    }
    if (target) {
      sessionStorage.setItem(SSO_TARGET_KEY, target);
      u.searchParams.delete("target");
      u.searchParams.delete("return_to");
      const qs = u.searchParams.toString();
      const cleanHash = hash.includes("?") ? hash.slice(0, hash.indexOf("?")) : hash;
      const h = cleanHash ? "#" + cleanHash : u.hash && !hash.includes("?") ? u.hash : "";
      history.replaceState(null, "", u.pathname + (qs ? "?" + qs : "") + h);
    }
  } catch {
    /* ignore */
  }
  return getSsoTarget();
}

export function getSsoTarget() {
  try {
    return (sessionStorage.getItem(SSO_TARGET_KEY) || "").trim();
  } catch {
    return "";
  }
}

export function clearSsoTarget() {
  try {
    sessionStorage.removeItem(SSO_TARGET_KEY);
  } catch {
    /* ignore */
  }
}

function isAllowedSsoHost(hostname) {
  const h = String(hostname || "").toLowerCase();
  if (!h) return false;
  if (SSO_HOST_ALLOW.includes(h)) return true;
  try {
    if (h === (window.location.hostname || "").toLowerCase()) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * @returns {"console"|"dist"|"unknown"}
 * 空目标 / 商家控制台 → console；分销管理台 → dist
 */
export function classifySsoTarget(target) {
  const raw = (target || "").trim();
  if (!raw) return "console";
  try {
    const u = new URL(raw, window.location.origin);
    if (!isAllowedSsoHost(u.hostname)) return "unknown";
    const path = (u.pathname || "").toLowerCase();
    if (path.includes("merchant-console") || path.endsWith("/merchant-console")) {
      return "console";
    }
    if (path.includes("/admin") || /dist\.ducai\.cc/i.test(u.hostname)) {
      return "dist";
    }
    if (u.origin === window.location.origin) return "console";
  } catch {
    return "unknown";
  }
  return "unknown";
}

export function paintSsoTargetHint() {
  const el = $("auth-target-hint");
  const title = $("login-title");
  const target = getSsoTarget();
  const kind = classifySsoTarget(target);
  if (!el) return;
  if (!target || kind === "console") {
    el.textContent = "";
    el.classList.add("hidden");
    if (title) title.textContent = "登录控制台";
    return;
  }
  if (kind === "dist") {
    const isInvite = /invite-join\.html/i.test(target);
    el.textContent = isInvite
      ? "登录成功后将完成分销商树邀请加入（dist_tree_join）"
      : "登录成功后将进入：分销管理公共平台";
    el.classList.remove("hidden");
    if (title) title.textContent = "统一登录";
    return;
  }
  el.textContent = "目标地址无效，登录后将进入商家控制台";
  el.classList.remove("hidden");
  if (title) title.textContent = "登录控制台";
}

/**
 * 构造分销**免登回跳**地址：目标 URL + `portal_token` + `merchant_id` / `person_id`。
 *
 * 与「登录后按 target 分流」共用同一套拼接口径（缺一不可：分销侧
 * `POST /auth/federation-login` 拿 portal_token 去开放平台自省换 Dist JWT）。
 *
 * 失败抛 `Error`，`err.code`：
 * - `NO_PORTAL_TOKEN`：接口密钥登录，无门户令牌——只有扫码会话能换票；
 * - `HOST_NOT_ALLOWED`：目标主机不在 `SSO_HOST_ALLOW`（防开放重定向）。
 *
 * `opts`（点击入口必传）：`{ token, merchantId, personId }` —— 用
 * `GET /merchant/dist-portal` **现签的短票**，而不是 localStorage 里的长票。
 * 长票可活 7 天，换商家/换人后再点按钮会拿旧票进别家租户
 * （2026-10-03 事故）。缺 `opts.token` 时才回落已存令牌（登录后按 target 分流
 * 的场景，令牌刚由本次登录写入）。
 */
export function buildDistHandoffUrl(target, opts) {
  const o = opts || {};
  const token = String(o.token || "").trim() || portalToken();
  if (!token) {
    const e = new Error("当前登录方式无法跳转分销，请使用扫码登录");
    e.code = "NO_PORTAL_TOKEN";
    throw e;
  }
  const u = new URL(target, window.location.origin);
  if (!isAllowedSsoHost(u.hostname)) {
    const e = new Error("分销目标主机未允许");
    e.code = "HOST_NOT_ALLOWED";
    throw e;
  }
  u.searchParams.set("portal_token", token);
  const mid = String(o.merchantId || "").trim() || localStorage.getItem(LS.merchantId) || "";
  const pid = String(o.personId || "").trim() || localStorage.getItem(LS.personId) || "";
  if (mid) u.searchParams.set("merchant_id", mid);
  if (pid) u.searchParams.set("person_id", pid);
  return u.toString();
}

/**
 * 认证通过后按 target 分流：
 * - 空 / 商家控制台 → 进控制台
 * - 分销 → 回跳分销（由分销侧校验权限）
 */
export async function afterAuthSuccess() {
  const target = getSsoTarget();
  const kind = classifySsoTarget(target);
  if (!target || kind === "console") {
    clearSsoTarget();
    await refreshAll();
    showConsole(true);
    return;
  }
  if (kind === "dist") {
    let href = "";
    try {
      href = buildDistHandoffUrl(target);
    } catch (e) {
      showAuthBanner(
        e && e.code === "NO_PORTAL_TOKEN"
          ? String(e.message)
          : "目标地址无效，已进入商家控制台",
        false
      );
      clearSsoTarget();
      await refreshAll();
      showConsole(true);
      return;
    }
    clearSsoTarget();
    showAuthBanner("登录成功，正在进入分销…", true);
    window.location.href = href;
    return;
  }
  clearSsoTarget();
  showAuthBanner("目标地址无效，已进入商家控制台", false);
  await refreshAll();
  showConsole(true);
}

/** MFG 身份联邦「绑定微信」成功后跳转带入：?mc_portal=&mc_merchant=&mc_base= */
export function consumePortalHandoff() {
  let token = "";
  let merchantId = "";
  let apiBaseHint = "";
  try {
    const u = new URL(window.location.href);
    token = (u.searchParams.get("mc_portal") || "").trim();
    merchantId = (u.searchParams.get("mc_merchant") || "").trim();
    apiBaseHint = (u.searchParams.get("mc_base") || "").trim().replace(/\/$/, "");
    if (token || merchantId || apiBaseHint) {
      ["mc_portal", "mc_merchant", "mc_base"].forEach((k) => u.searchParams.delete(k));
      const qs = u.searchParams.toString();
      const clean = u.pathname + (qs ? "?" + qs : "") + (u.hash || "");
      history.replaceState(null, "", clean);
    }
  } catch {
    /* ignore */
  }
  if (apiBaseHint) {
    localStorage.setItem(LS.base, apiBaseHint);
    const base = $("base-url");
    if (base) base.value = apiBaseHint;
  }
  if (token && merchantId) {
    setAuthSession({ merchantId, portalToken: token, mode: "portal" });
    return true;
  }
  return false;
}

export function isLoggedIn() {
  const mid = $("merchant-id").value.trim() || localStorage.getItem(LS.merchantId);
  if (!mid) return false;
  if (authMode() === "portal" && portalToken()) return true;
  return !!$("merchant-key").value.trim() || !!localStorage.getItem(LS.key);
}

export function showAuth() {
  $("auth-screen").classList.remove("hidden");
  $("console-screen").classList.add("hidden");
  stopPoll();
}

export function showConsole(skipRefresh) {
  $("auth-screen").classList.add("hidden");
  $("console-screen").classList.remove("hidden");
  const h = authHash();
  if (isAuthHash(h) || !h) {
    history.replaceState(null, "", "#" + defaultConsoleRoute());
  }
  if (!skipRefresh) refreshAll();
  ping();
}

function showAuthBanner(text, ok) {
  const el = $("auth-banner");
  el.textContent = text;
  el.classList.toggle("hidden", !text);
  el.classList.toggle("ok", !!ok);
}

export function stopPoll() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function applyQrToImg(imgEl, dataUrl) {
  if (!imgEl) return;
  imgEl.onerror = () => {
    const pane = imgEl.id === "reg-qr-img" ? "reg" : "login";
    setQrStatus(pane, "二维码加载失败，请刷新", true);
  };
  if (dataUrl) {
    imgEl.src = dataUrl;
    imgEl.classList.remove("hidden");
  }
}

function setQrStatus(pane, text, isErr) {
  const id = pane === "reg" ? "reg-qr-status" : "login-qr-status";
  const el = $(id);
  const imgId = pane === "reg" ? "reg-qr-img" : "login-qr-img";
  const frame = $(imgId) ? $(imgId).closest(".mc-qr-frame") : null;
  if (frame) frame.classList.remove("is-loading");
  if (!el) return;
  if (!text) {
    el.textContent = "";
    el.classList.add("hidden");
    el.classList.remove("err");
    return;
  }
  el.textContent = text;
  el.classList.remove("hidden");
  el.classList.toggle("err", !!isErr);
}

function setQrLoading(pane, loading) {
  const imgId = pane === "reg" ? "reg-qr-img" : "login-qr-img";
  const img = $(imgId);
  const frame = img ? img.closest(".mc-qr-frame") : null;
  if (frame) frame.classList.toggle("is-loading", !!loading);
  const refreshId = pane === "reg" ? "btn-reg-qr-refresh" : "btn-login-qr-refresh";
  const refreshBtn = $(refreshId);
  if (refreshBtn) refreshBtn.disabled = !!loading;
}

async function startQrSession(intent, provider, extra = {}) {
  stopPoll();
  resetMerchantChoiceView();
  const pane = intent === "register" ? "reg" : "login";
  setQrLoading(pane, true);
  const body = {
    provider,
    intent,
    tenant_kind: extra.tenant_kind || "platform_mall",
    shop_name: extra.shop_name || null,
    channel: extra.channel || "other",
    base_url: $("base-url").value.trim() || window.location.origin,
  };
  if (extra.contact) body.contact = extra.contact;
  if (extra.sms_ticket) body.sms_ticket = extra.sms_ticket;
  const { ok, data } = await apiPublic("POST", "/api/v1/merchant/auth/qr/start", body);
  setQrLoading(pane, false);
  if (!ok || !data || !data.session_id) {
    const msg = errMsg(data, "无法创建扫码会话");
    setQrStatus(pane, msg, true);
    showAuthBanner(msg, false);
    return null;
  }
  qrFinishing = false;
  qrSession = {
    id: data.session_id,
    intent,
    mode: data.mode || "mock",
    provider,
  };
  document.querySelectorAll(".mc-dev-only").forEach((el) => {
    el.classList.toggle("hidden", data.mode !== "mock");
  });
  return data;
}

function handleQrComplete(data, intent) {
  if (data.status === "failed") {
    showAuthBanner(data.message || data.error_code || "授权失败", false);
    if (data.error_code === "OAUTH_ALREADY_REGISTERED" && data.merchant_id) {
      setTimeout(() => showLoginView(), 2000);
    }
    return;
  }
  if (data.status !== "completed") return;
  if (qrFinishing) return;
  qrFinishing = true;
  resetMerchantChoiceView();

  if (data.portal_token) {
    setAuthSession({
      merchantId: data.merchant_id,
      personId: data.person_id || undefined,
      portalToken: data.portal_token,
      mode: "portal",
    });
  } else if (data.api_key) {
    setAuthSession({
      merchantId: data.merchant_id,
      personId: data.person_id || undefined,
      apiKey: data.api_key,
      mode: "api_key",
    });
    setEnv("sandbox");
      showAuthBanner(`Sandbox Key 已保存（仅此一次）：${data.api_key}`, true);
  } else if (data.merchant_id) {
    setAuthSession({
      merchantId: data.merchant_id,
      personId: data.person_id || undefined,
      mode: "api_key",
    });
  }

  stopPoll();
  const pane = intent === "register" ? "reg" : "login";
  setQrStatus(pane, "", false);
  showAuthBanner(intent === "register" ? "入驻成功，正在进入控制台…" : "登录成功", true);
  setTimeout(async () => {
    await afterAuthSuccess();
  }, 400);
}

function renewDeadQr(pane, reason) {
  const intent = qrSession.intent === "register" ? "register" : "login";
  const provider = qrSession.provider;
  const mode = qrSession.mode;
  stopPoll();
  qrSession = { id: null, intent, mode, provider };
  setQrStatus(pane, reason, true);
  showAuthBanner(reason, false);
  if (_qrAutoRefreshing) return;
  _qrAutoRefreshing = true;
  const renew = intent === "register" ? refreshRegisterQr : refreshLoginQr;
  Promise.resolve()
    .then(() => renew())
    .catch(() => {})
    .finally(() => {
      _qrAutoRefreshing = false;
    });
}

/** 一人多组织：提示在手机上选商家。轮询不中断，手机选定后本页直接进入。 */
function renderMerchantChoice(data) {
  const box = $("login-choose-merchant");
  const list = $("login-choose-list");
  if (!box || !list) return;
  const sig = (data.candidates || []).map((c) => c.merchant_id).join(",");
  if (box.dataset.sig === sig && !box.classList.contains("hidden")) return;
  box.dataset.sig = sig;
  const hint = $("login-choose-hint");
  if (hint) {
    hint.textContent = "请在手机上选择商家，选定后本页自动进入";
  }
  list.innerHTML = "";
  (data.candidates || []).forEach((c) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "secondary mc-choose-item";
    btn.dataset.merchantId = c.merchant_id;
    const name = document.createElement("strong");
    name.textContent = c.shop_name || c.merchant_id;
    const mid = document.createElement("span");
    mid.className = "mc-desc";
    mid.textContent = c.merchant_id;
    btn.appendChild(name);
    btn.appendChild(mid);
    btn.addEventListener("click", () => submitMerchantChoice(c.merchant_id, btn));
    list.appendChild(btn);
  });
  const img = $("login-qr-img");
  const frame = img ? img.closest(".mc-qr-frame") : null;
  if (frame) frame.classList.add("hidden");
  const actions = document.querySelector("#login-qr .mc-qr-actions");
  if (actions) actions.classList.add("hidden");
  box.classList.remove("hidden");
  setQrStatus("login", "", false);
}

/** 还原扫码区（刷新二维码 / 换会话时调用） */
function resetMerchantChoiceView() {
  const box = $("login-choose-merchant");
  if (box) {
    box.classList.add("hidden");
    delete box.dataset.sig;
  }
  const img = $("login-qr-img");
  const frame = img ? img.closest(".mc-qr-frame") : null;
  if (frame) frame.classList.remove("hidden");
  const actions = document.querySelector("#login-qr .mc-qr-actions");
  if (actions) actions.classList.remove("hidden");
}

/** 提交选定商家 → 服务端回查候选后签发 portal_token，复用既有完成流程 */
async function submitMerchantChoice(merchantId, btn) {
  if (!qrSession.id) return;
  if (btn) btn.disabled = true;
  const { ok, data } = await apiPublic(
    "POST",
    `/api/v1/merchant/auth/qr/${encodeURIComponent(qrSession.id)}/choose-merchant`,
    { merchant_id: merchantId }
  );
  if (btn) btn.disabled = false;
  if (!ok || !data || data.status !== "completed") {
    showAuthBanner(errMsg(data, "选择商家失败，请刷新二维码重试"), false);
    return;
  }
  resetMerchantChoiceView();
  handleQrComplete(data, qrSession.intent);
}

function pollQrSession() {
  if (!qrSession.id) return;
  const pane = qrSession.intent === "register" ? "reg" : "login";
  const choosingBox = $("login-choose-merchant");
  const choosing = !!(choosingBox && !choosingBox.classList.contains("hidden"));
  const waiting =
    qrSession.mode === "wxa" && qrSession.provider === "wechat"
      ? "等待微信小程序确认…"
      : "等待扫码确认…";
  if (!choosing) setQrStatus(pane, waiting, false);
  apiPublic("GET", `/api/v1/merchant/auth/qr/${encodeURIComponent(qrSession.id)}`).then(
    ({ ok, status, data }) => {
      const code = errCode(data);
      const dead =
        status === 404 ||
        code === "AUTH_SESSION_NOT_FOUND" ||
        (data && data.status === "expired");
      if (dead) {
        renewDeadQr(pane, "二维码已失效，正在自动换新码…");
        return;
      }
      if (!ok || !data) {
        // 502/网络抖动：停一轮提示，保留会话 id，下个 interval 再试
        setQrStatus(pane, errMsg(data, "轮询失败，请稍后或点刷新"), true);
        return;
      }
      if (data.status === "choose_merchant") {
        renderMerchantChoice(data);
        return;
      }
      if (data.status === "pending" || data.status === "scanned") {
        setQrStatus(
          pane,
          data.status === "scanned"
            ? "已扫码，请在小程序内确认…"
            : qrSession.mode === "wxa"
              ? "等待微信扫小程序码…"
              : "等待扫码…",
          false
        );
        return;
      }
      handleQrComplete(data, qrSession.intent);
    }
  );
}

function beginPoll() {
  stopPoll();
  pollQrSession();
  pollTimer = setInterval(pollQrSession, 3000);
}

async function refreshLoginQr() {
  const hint = $("login-qr-hint");
  setQrLoading("login", true);
  const data = await startQrSession("login", loginProvider);
  if (!data) return;
  const mode = data.mode || "mock";
  if (hint) {
    if (loginProvider === "wechat" && mode === "wxa") {
      hint.textContent = "请用微信扫一扫打开度裁小程序完成登录";
    } else {
      hint.textContent = `${PROVIDER_HINT[loginProvider]}登录`;
    }
  }
  applyQrToImg($("login-qr-img"), data.qr_image_data_url);
  setQrStatus(
    "login",
    mode === "wxa" ? "请使用微信扫小程序码" : "请使用手机扫码",
    false
  );
  beginPoll();
}

let _regSms = null;
let _regSmsSendCooldown = 0;
let _regSmsSendTimer = null;

async function refreshRegisterQr() {
  const hint = $("reg-qr-hint");
  if (!_regSms || !_regSms.ticket || !_regSms.phone) {
    showAuthBanner("请先完成手机号短信验证", false);
    showRegStep(2);
    return;
  }
  setQrLoading("reg", true);
  const data = await startQrSession("register", regProvider, {
    tenant_kind: $("reg-tenant-kind").value,
    shop_name: $("reg-shop").value.trim(),
    channel: "other",
    contact: _regSms.phone,
    sms_ticket: _regSms.ticket,
  });
  if (!data) return;
  const mode = data.mode || "mock";
  if (hint) {
    if (regProvider === "wechat" && mode === "wxa") {
      hint.textContent = "请用微信扫一扫打开度裁小程序完成入驻授权";
    } else {
      hint.textContent = `${PROVIDER_HINT[regProvider]}完成入驻授权`;
    }
  }
  applyQrToImg($("reg-qr-img"), data.qr_image_data_url);
  setQrStatus(
    "reg",
    mode === "wxa" ? "请使用微信扫小程序码完成授权" : "请使用手机扫码完成授权",
    false
  );
  beginPoll();
}

async function mockScan() {
  if (!qrSession.id) return;
  const { ok, data } = await apiPublic(
    "POST",
    `/api/v1/merchant/auth/qr/${encodeURIComponent(qrSession.id)}/mock-scan`,
    { nickname: "IT老民工" }
  );
  if (ok && data) handleQrComplete(data, qrSession.intent);
  else showAuthBanner((data && data.message) || "模拟扫码失败", false);
}

export function showLoginView(updateHash = true) {
  stopPoll();
  const login = $("view-login");
  const reg = $("view-register");
  if (login) login.classList.remove("hidden");
  if (reg) reg.classList.add("hidden");
  showAuthBanner("", false);
  paintSsoTargetHint();
  if (updateHash) setAuthHash("login");
  refreshLoginQr();
}

export function showRegisterView(updateHash = true) {
  stopPoll();
  const login = $("view-login");
  const reg = $("view-register");
  if (login) login.classList.add("hidden");
  if (reg) reg.classList.remove("hidden");
  showAuthBanner("", false);
  if (updateHash) setAuthHash("register");
  _regSms = null;
  _regSmsSendCooldown = 0;
  if (_regSmsSendTimer) {
    clearInterval(_regSmsSendTimer);
    _regSmsSendTimer = null;
  }
  const sendBtn = $("btn-reg-sms-send");
  if (sendBtn) {
    sendBtn.disabled = false;
    sendBtn.textContent = "获取验证码";
  }
  showRegStep(1);
}

function showRegStep(n) {
  $("reg-step-1").classList.toggle("hidden", n !== 1);
  $("reg-step-2").classList.toggle("hidden", n !== 2);
  $("reg-step-3").classList.toggle("hidden", n !== 3);
  const steps = $("register-steps");
  if (steps) {
    const spans = steps.querySelectorAll("span");
    spans.forEach((sp, i) => {
      sp.classList.remove("done", "current");
      if (i + 1 < n) sp.classList.add("done");
      if (i + 1 === n) sp.classList.add("current");
    });
  }
  if (n === 3) refreshRegisterQr();
  else stopPoll();
}

// ── Login tabs ──
document.querySelectorAll("#view-login .mc-auth-tabs .mc-tab").forEach((btn) => {
  btn.addEventListener("click", () => {
    document.querySelectorAll("#view-login .mc-auth-tabs .mc-tab").forEach((b) => {
      b.classList.toggle("active", b === btn);
    });
    const pane = btn.dataset.pane;
    $("login-qr").classList.toggle("hidden", pane !== "login-qr");
    const smsPane = $("login-sms");
    if (smsPane) smsPane.classList.toggle("hidden", pane !== "login-sms");
    if (pane === "login-qr") refreshLoginQr();
    else stopPoll();
  });
});

document.querySelectorAll("#login-qr .mc-qr-provider-switch button").forEach((btn) => {
  btn.addEventListener("click", () => {
    document.querySelectorAll("#login-qr .mc-qr-provider-switch button").forEach((b) => {
      b.classList.toggle("active", b === btn);
    });
    loginProvider = btn.dataset.provider || "wechat";
    refreshLoginQr();
  });
});

document.querySelectorAll("[data-reg-provider]").forEach((btn) => {
  btn.addEventListener("click", () => {
    document.querySelectorAll("[data-reg-provider]").forEach((b) => {
      b.classList.toggle("active", b === btn);
    });
    regProvider = btn.dataset.provider || "wechat";
    refreshRegisterQr();
  });
});

bindClick("go-register", (e) => {
  e.preventDefault();
  showRegisterView();
});
bindClick("go-login", (e) => {
  e.preventDefault();
  showLoginView();
});
$("btn-login-qr-refresh").addEventListener("click", refreshLoginQr);
$("btn-reg-qr-refresh").addEventListener("click", refreshRegisterQr);
$("btn-login-mock-scan").addEventListener("click", mockScan);
$("btn-reg-mock-scan").addEventListener("click", mockScan);

$("btn-reg-next-1").addEventListener("click", () => showRegStep(2));
$("btn-reg-back-2").addEventListener("click", () => showRegStep(1));
$("btn-reg-next-2").addEventListener("click", async () => {
  const shop = ($("reg-shop") && $("reg-shop").value.trim()) || "";
  const phoneEl = $("reg-phone");
  const codeEl = $("reg-sms-code");
  const phone = phoneEl ? phoneEl.value.trim() : "";
  const code = codeEl ? codeEl.value.trim() : "";
  if (!shop) {
    showAuthBanner("请填写店名", false);
    return;
  }
  if (!/^\d{11}$/.test(phone)) {
    showAuthBanner("请填写正确的 11 位手机号", false);
    return;
  }
  if (_regSms && _regSms.phone === phone && _regSms.ticket) {
    showRegStep(3);
    return;
  }
  if (!code) {
    showAuthBanner("请填写短信验证码", false);
    return;
  }
  syncSmsPublicBase();
  const btn = $("btn-reg-next-2");
  if (btn) btn.disabled = true;
  try {
    const { ok, data } = await apiPublic(
      "POST",
      "/api/v1/merchant/auth/sms/register/verify",
      { phone, code }
    );
    if (!ok || !data || !data.sms_ticket) {
      showAuthBanner(errMsg(data, "短信验证失败"), false);
      return;
    }
    _regSms = { phone: data.phone || phone, ticket: data.sms_ticket };
    showAuthBanner("手机号验证通过", true);
    showRegStep(3);
  } finally {
    if (btn) btn.disabled = false;
  }
});
$("btn-reg-back-3").addEventListener("click", () => showRegStep(2));

bindClick("btn-reg-sms-send", async () => {
  const phoneEl = $("reg-phone");
  const phone = phoneEl ? phoneEl.value.trim() : "";
  if (!/^\d{11}$/.test(phone)) {
    showAuthBanner("请填写正确的 11 位手机号", false);
    return;
  }
  const btn = $("btn-reg-sms-send");
  if (btn && _regSmsSendCooldown > 0) return;
  if (_regSms && _regSms.phone !== phone) _regSms = null;
  syncSmsPublicBase();
  if (btn) btn.disabled = true;
  const { ok, data } = await apiPublic(
    "POST",
    "/api/v1/merchant/auth/sms/register/send",
    { phone }
  );
  if (!ok) {
    if (btn) btn.disabled = false;
    showAuthBanner(errMsg(data, "验证码发送失败"), false);
    return;
  }
  if (data && data.dev && data.code) {
    const codeEl = $("reg-sms-code");
    if (codeEl) codeEl.value = String(data.code);
    showAuthBanner(`开发模式验证码：${data.code}`, true);
  } else {
    showAuthBanner("验证码已发送", true);
  }
  _regSmsSendCooldown = 60;
  if (_regSmsSendTimer) clearInterval(_regSmsSendTimer);
  const tick = () => {
    if (!btn) return;
    if (_regSmsSendCooldown <= 0) {
      clearInterval(_regSmsSendTimer);
      _regSmsSendTimer = null;
      btn.disabled = false;
      btn.textContent = "获取验证码";
      return;
    }
    btn.disabled = true;
    btn.textContent = `${_regSmsSendCooldown}s`;
    _regSmsSendCooldown -= 1;
  };
  tick();
  _regSmsSendTimer = setInterval(tick, 1000);
});

const regPhoneEl = $("reg-phone");
if (regPhoneEl) {
  regPhoneEl.addEventListener("input", () => {
    const phone = regPhoneEl.value.trim();
    if (_regSms && _regSms.phone !== phone) _regSms = null;
  });
}

let _smsTicket = null;
let _smsSendCooldown = 0;
let _smsSendTimer = null;

function syncSmsPublicBase() {
  const origin = (window.location.origin || "").replace(/\/$/, "");
  const baseEl = $("base-url");
  if (origin && baseEl) {
    try {
      if (!isLocalDevHost(new URL(origin).hostname)) {
        baseEl.value = origin;
        localStorage.setItem(LS.base, origin);
      }
    } catch {
      /* ignore */
    }
  }
}

function resetSmsChooseView() {
  _smsTicket = null;
  const box = $("login-sms-choose-merchant");
  if (box) {
    box.classList.add("hidden");
    delete box.dataset.sig;
  }
  const list = $("login-sms-choose-list");
  if (list) list.innerHTML = "";
}

async function finishSmsLogin(data) {
  if (!data || !data.merchant_id || !data.portal_token) {
    showAuthBanner(errMsg(data, "登录失败"), false);
    return;
  }
  resetSmsChooseView();
  $("merchant-id").value = data.merchant_id;
  setAuthSession({
    merchantId: data.merchant_id,
    personId: data.person_id || undefined,
    portalToken: data.portal_token,
    mode: "portal",
  });
  const me = data.me || data;
  if (window.McAuth && me) window.McAuth.setMe(me);
  await afterAuthSuccess();
  if (me && me.note) {
    showAuthBanner(String(me.note), true);
  }
}

function renderSmsMerchantChoice(data) {
  const box = $("login-sms-choose-merchant");
  const list = $("login-sms-choose-list");
  if (!box || !list) return;
  _smsTicket = data.sms_ticket || null;
  const sig = (data.candidates || []).map((c) => c.merchant_id).join(",");
  box.dataset.sig = sig;
  const hint = $("login-sms-choose-hint");
  if (hint) {
    hint.textContent = data.message || "该账号关联多个商家，请选择本次进入的商家";
  }
  list.innerHTML = "";
  (data.candidates || []).forEach((c) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "secondary mc-choose-item";
    btn.dataset.merchantId = c.merchant_id;
    const name = document.createElement("strong");
    name.textContent = c.shop_name || c.merchant_id;
    const mid = document.createElement("span");
    mid.className = "mc-desc";
    mid.textContent = c.merchant_id;
    btn.appendChild(name);
    btn.appendChild(mid);
    btn.addEventListener("click", async () => {
      if (!_smsTicket) {
        showAuthBanner("登录会话已失效，请重新获取验证码", false);
        return;
      }
      btn.disabled = true;
      const { ok, data: chosen } = await apiPublic(
        "POST",
        "/api/v1/merchant/auth/sms/choose-merchant",
        { sms_ticket: _smsTicket, merchant_id: c.merchant_id },
      );
      if (!ok || !chosen || !chosen.portal_token) {
        btn.disabled = false;
        showAuthBanner(errMsg(chosen, "选定商家失败"), false);
        return;
      }
      await finishSmsLogin(chosen);
    });
    list.appendChild(btn);
  });
  box.classList.remove("hidden");
}

bindClick("btn-sms-send", async () => {
  const phoneEl = $("login-sms-phone");
  const phone = phoneEl ? phoneEl.value.trim() : "";
  if (!/^\d{11}$/.test(phone)) {
    showAuthBanner("请填写正确的 11 位手机号", false);
    return;
  }
  const btn = $("btn-sms-send");
  if (btn && _smsSendCooldown > 0) return;
  syncSmsPublicBase();
  if (btn) btn.disabled = true;
  const { ok, data } = await apiPublic("POST", "/api/v1/merchant/auth/sms/send", { phone });
  if (!ok) {
    if (btn) btn.disabled = false;
    showAuthBanner(errMsg(data, "验证码发送失败"), false);
    return;
  }
  if (data && data.dev && data.code) {
    const codeEl = $("login-sms-code");
    if (codeEl) codeEl.value = String(data.code);
    showAuthBanner(`开发模式验证码：${data.code}`, true);
  } else {
    showAuthBanner("验证码已发送", true);
  }
  _smsSendCooldown = 60;
  if (_smsSendTimer) clearInterval(_smsSendTimer);
  const tick = () => {
    if (!btn) return;
    if (_smsSendCooldown <= 0) {
      clearInterval(_smsSendTimer);
      _smsSendTimer = null;
      btn.disabled = false;
      btn.textContent = "获取验证码";
      return;
    }
    btn.disabled = true;
    btn.textContent = `${_smsSendCooldown}s`;
    _smsSendCooldown -= 1;
  };
  tick();
  _smsSendTimer = setInterval(tick, 1000);
});

bindClick("btn-sms-login", async () => {
  const phoneEl = $("login-sms-phone");
  const codeEl = $("login-sms-code");
  const phone = phoneEl ? phoneEl.value.trim() : "";
  const code = codeEl ? codeEl.value.trim() : "";
  if (!/^\d{11}$/.test(phone)) {
    showAuthBanner("请填写正确的 11 位手机号", false);
    return;
  }
  if (!code) {
    showAuthBanner("请填写验证码", false);
    return;
  }
  syncSmsPublicBase();
  resetSmsChooseView();
  const { ok, data } = await apiPublic("POST", "/api/v1/merchant/auth/sms/login", {
    phone,
    code,
  });
  if (!ok) {
    showAuthBanner(errMsg(data, "登录失败，请检查手机号与验证码"), false);
    return;
  }
  if (data && data.status === "choose_merchant") {
    renderSmsMerchantChoice(data);
    showAuthBanner(data.message || "请选择商家", true);
    return;
  }
  await finishSmsLogin(data);
});

$("btn-logout").addEventListener("click", () => {
  clearAuthSession();
  stopPoll();
  showAuth();
  showLoginView();
});

export function bindAuthUi() {
  // listeners attached at module load (legacy parity)
}

